// src/hooks/useCalendarTimeRange.js
import { useState, useEffect } from 'react';

const STORAGE_KEY = 'calendar_display_hours';
const DEFAULT_RANGE = { start: 8, end: 18 };

/**
 * Custom hook for managing calendar time range display preference
 * Stores user's preference in localStorage for persistence
 *
 * @returns {Object} - { timeRange, setTimeRange, resetTimeRange }
 */
export const useCalendarTimeRange = () => {
  const [timeRange, setTimeRangeState] = useState(() => {
    // Initialize from localStorage on mount
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate the saved data
        if (
          typeof parsed.start === 'number' &&
          typeof parsed.end === 'number' &&
          parsed.start >= 0 &&
          parsed.end <= 24 &&
          parsed.start < parsed.end
        ) {
          return parsed;
        }
      }
    } catch (error) {
      console.warn('Failed to load calendar time range from localStorage:', error);
    }
    return DEFAULT_RANGE;
  });

  // Save to localStorage whenever timeRange changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(timeRange));
    } catch (error) {
      console.warn('Failed to save calendar time range to localStorage:', error);
    }
  }, [timeRange]);

  const setTimeRange = (newRange) => {
    // Validate before setting
    if (
      typeof newRange.start === 'number' &&
      typeof newRange.end === 'number' &&
      newRange.start >= 0 &&
      newRange.end <= 24 &&
      newRange.start < newRange.end
    ) {
      setTimeRangeState(newRange);
    } else {
      console.warn('Invalid time range:', newRange);
    }
  };

  const resetTimeRange = () => {
    setTimeRangeState(DEFAULT_RANGE);
  };

  return {
    timeRange,
    setTimeRange,
    resetTimeRange
  };
};

export default useCalendarTimeRange;
